import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES = 20;

interface MediaItem {
  id: number;
  url: string;
  data_url: string;
  thumbnail_url: string;
  is_cover: boolean;
  position: number;
  filename: string;
}

interface MediaListResponse {
  data: MediaItem[];
}

interface MediaUploadResponse {
  data: MediaItem;
}

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
}

interface MediaUploadProps {
  propertyId: number | null;
}

export default function MediaUpload({ propertyId }: MediaUploadProps): React.ReactElement {
  const { t } = useTranslation('properties');
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deleteModalRef = useRef<HTMLDialogElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; mediaId: number | null }>({
    open: false,
    mediaId: null,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // --- Auto-dismiss snackbar ---
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  // --- Sync delete modal ---
  useEffect(() => {
    if (deleteDialog.open) deleteModalRef.current?.showModal();
    else deleteModalRef.current?.close();
  }, [deleteDialog.open]);

  const { data: mediaData } = useQuery({
    queryKey: ['property-media', propertyId],
    queryFn: () => api.get<MediaListResponse>(`/v1/properties/${propertyId}/media`),
    enabled: !!propertyId,
  });

  const mediaItems = mediaData?.data ?? [];

  const showSnackbar = (message: string, severity: 'success' | 'error'): void => {
    setSnackbar({ open: true, message, severity });
  };

  const uploadFile = useCallback(
    async (file: File): Promise<void> => {
      if (!propertyId) return;

      const formData = new FormData();
      formData.append('file', file);

      setUploadingFiles((prev) => [...prev, { file, progress: 0 }]);

      try {
        await api.upload<MediaUploadResponse>(
          `/v1/properties/${propertyId}/media`,
          formData,
          (progress) => {
            setUploadingFiles((prev) =>
              prev.map((uf) => (uf.file === file ? { ...uf, progress } : uf))
            );
          }
        );
        queryClient.invalidateQueries({ queryKey: ['property-media', propertyId] });
      } catch {
        setUploadingFiles((prev) =>
          prev.map((uf) => (uf.file === file ? { ...uf, error: t('media.uploadError') } : uf))
        );
        showSnackbar(t('media.uploadError'), 'error');
      } finally {
        // Remove from uploading list after a brief delay so user sees 100%
        setTimeout(() => {
          setUploadingFiles((prev) => prev.filter((uf) => uf.file !== file));
        }, 1000);
      }
    },
    [propertyId, queryClient, t]
  );

  const processFiles = useCallback(
    (files: FileList | File[]): void => {
      if (!propertyId) {
        showSnackbar(t('media.saveFirst'), 'error');
        return;
      }

      const currentCount = mediaItems.length + uploadingFiles.length;
      const fileArray = Array.from(files);

      for (const file of fileArray) {
        if (currentCount + fileArray.indexOf(file) >= MAX_IMAGES) {
          showSnackbar(t('media.maxReached'), 'error');
          break;
        }

        if (!ACCEPTED_TYPES.includes(file.type)) {
          showSnackbar(t('media.invalidType'), 'error');
          continue;
        }

        if (file.size > MAX_FILE_SIZE) {
          showSnackbar(t('media.tooLarge'), 'error');
          continue;
        }

        uploadFile(file);
      }
    },
    [propertyId, mediaItems.length, uploadingFiles.length, uploadFile, t]
  );

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      // Reset input so the same file can be selected again
      e.target.value = '';
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (mediaId: number) =>
      api.delete(`/v1/properties/${propertyId}/media/${mediaId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-media', propertyId] });
      setDeleteDialog({ open: false, mediaId: null });
    },
    onError: () => {
      showSnackbar(t('media.deleteError'), 'error');
    },
  });

  const coverMutation = useMutation({
    mutationFn: (mediaId: number) =>
      api.patch(`/v1/properties/${propertyId}/media/${mediaId}/cover`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-media', propertyId] });
      showSnackbar(t('media.coverSuccess'), 'success');
    },
    onError: () => {
      showSnackbar(t('media.coverError'), 'error');
    },
  });

  const handleDeleteClick = (mediaId: number): void => {
    setDeleteDialog({ open: true, mediaId });
  };

  const handleDeleteConfirm = (): void => {
    if (deleteDialog.mediaId) {
      deleteMutation.mutate(deleteDialog.mediaId);
    }
  };

  const handleSetCover = (mediaId: number): void => {
    coverMutation.mutate(mediaId);
  };

  /**
   * Resolve the best available image source for a media item.
   * Prefer data_url (base64) if present, fall back to thumbnail_url or url.
   */
  const getImageSrc = (item: MediaItem): string => {
    if (item.data_url) return item.data_url;
    return item.thumbnail_url || item.url;
  };

  if (!propertyId) {
    return (
      <div className="text-center py-8">
        <p className="text-base-content/60">{t('media.saveFirst')}</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-medium mb-1">{t('media.title')}</h3>
      <p className="text-sm text-base-content/60 mb-4">
        {t('media.count', { current: mediaItems.length, max: MAX_IMAGES })}
      </p>

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all mb-4
          ${isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-base-300 bg-base-100 hover:border-primary/50 hover:bg-base-200/50'
          }`}
      >
        {/* Upload cloud icon */}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-base-content/40 mb-2" viewBox="0 0 20 20" fill="currentColor">
          <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
          <path d="M9 13h2v5a1 1 0 11-2 0v-5z" />
        </svg>
        <p className="text-base-content">
          {isDragOver ? t('media.dropzoneActive') : t('media.dropzone')}
        </p>
        <p className="text-xs text-base-content/50 mt-1">
          {t('media.formats')}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload progress */}
      {uploadingFiles.length > 0 && (
        <div className="mb-4 space-y-2">
          {uploadingFiles.map((uf, index) => (
            <div key={`uploading-${index}`}>
              <p className="text-xs truncate mb-1">{uf.file.name}</p>
              <progress
                className={`progress w-full ${uf.error ? 'progress-error' : 'progress-primary'}`}
                value={uf.progress}
                max="100"
              />
            </div>
          ))}
        </div>
      )}

      {/* Thumbnail grid */}
      {mediaItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
          {mediaItems.map((item) => (
            <div key={item.id} className="relative group rounded-lg overflow-hidden">
              <img
                src={getImageSrc(item)}
                alt={item.filename}
                loading="lazy"
                className="w-full h-40 object-cover"
              />
              {/* Overlay with actions */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  className={`btn btn-circle btn-xs ${item.is_cover ? 'text-warning' : 'text-white/70 hover:text-warning'}`}
                  onClick={() => handleSetCover(item.id)}
                  title={item.is_cover ? t('media.isCover') : t('media.setCover')}
                >
                  {item.is_cover ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-circle btn-xs text-white/70 hover:text-error"
                  onClick={() => handleDeleteClick(item.id)}
                  title={t('media.delete')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              {/* Cover badge */}
              {item.is_cover && (
                <span className="absolute top-1 left-1 badge badge-warning badge-sm font-bold">
                  {t('media.isCover')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      <dialog ref={deleteModalRef} className="modal" onClose={() => setDeleteDialog({ open: false, mediaId: null })}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">{t('media.delete')}</h3>
          <p className="py-4 text-base-content/70">{t('media.deleteConfirm')}</p>
          <div className="modal-action">
            <button className="btn" onClick={() => setDeleteDialog({ open: false, mediaId: null })}>
              {t('media.delete', { ns: 'common', defaultValue: 'Cancel' })}
            </button>
            <button
              className="btn btn-error"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : t('media.delete')}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setDeleteDialog({ open: false, mediaId: null })}>close</button>
        </form>
      </dialog>

      {/* Toast / Snackbar */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
